import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Save, CreditCard, Wallet, CheckCircle, XCircle, Clock, Loader2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSettingsTab = ({ token }) => {
  const [settings, setSettings] = useState({
    stripe_secret_key: '',
    stripe_publishable_key: '',
    stripe_webhook_secret: '',
    stripe_payment_enabled: false,
    usdt_wallet_address: '',
    usdt_network: 'TRC20',
    usdt_payment_enabled: false,
    payment_instructions: '',
    paywb_payment_enabled: false,
    usd_try_rate: 0,
    paywb_fx_margin_pct: 0,
    rate_source: 'tcmb',
    tcmb_rate_cache: null
  });
  const [pendingPayments, setPendingPayments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSecrets, setShowSecrets] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/payments/settings`, { headers }),
        axios.get(`${API}/payments/admin/all?status=pending`, { headers })
      ]);
      setSettings(settingsRes.data);
      setPendingPayments(paymentsRes.data);
    } catch (err) {
      console.error('Error fetching payment settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { ...settings };
      delete data.stripe_secret_key_masked;
      delete data.stripe_webhook_secret_masked;
      // Don't send masked values
      if (data.stripe_secret_key && data.stripe_secret_key.startsWith('****')) {
        delete data.stripe_secret_key;
      }
      if (data.stripe_webhook_secret && data.stripe_webhook_secret.startsWith('****')) {
        delete data.stripe_webhook_secret;
      }
      await axios.put(`${API}/payments/settings`, data, { headers });
      alert('Payment settings saved!');
      fetchData();
    } catch (err) {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const verifyPayment = async (paymentId) => {
    try {
      await axios.put(`${API}/payments/admin/${paymentId}/verify`, {}, { headers });
      setPendingPayments(prev => prev.filter(p => p.id !== paymentId));
    } catch (err) {
      alert('Failed to verify payment');
    }
  };

  const rejectPayment = async (paymentId) => {
    try {
      await axios.put(`${API}/payments/admin/${paymentId}/reject`, {}, { headers });
      setPendingPayments(prev => prev.filter(p => p.id !== paymentId));
    } catch (err) {
      alert('Failed to reject payment');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Save Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Payment Settings</h2>
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Stripe Configuration */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Stripe (Credit Card)</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-zinc-500 text-xs">{settings.stripe_payment_enabled ? 'Enabled' : 'Disabled'}</span>
            <Switch
              checked={settings.stripe_payment_enabled}
              onCheckedChange={(val) => setSettings({ ...settings, stripe_payment_enabled: val })}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Publishable Key</Label>
            <Input
              value={settings.stripe_publishable_key || ''}
              onChange={(e) => setSettings({ ...settings, stripe_publishable_key: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
              placeholder="pk_live_..."
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300">Secret Key</Label>
              <button onClick={() => setShowSecrets(!showSecrets)} className="text-zinc-500 hover:text-zinc-300 transition-colors">
                {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Input
              type={showSecrets ? 'text' : 'password'}
              value={settings.stripe_secret_key || ''}
              onChange={(e) => setSettings({ ...settings, stripe_secret_key: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
              placeholder="sk_live_..."
            />
            {settings.stripe_secret_key_masked && !settings.stripe_secret_key && (
              <p className="text-zinc-500 text-xs">Current: {settings.stripe_secret_key_masked}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Webhook Secret</Label>
            <Input
              type={showSecrets ? 'text' : 'password'}
              value={settings.stripe_webhook_secret || ''}
              onChange={(e) => setSettings({ ...settings, stripe_webhook_secret: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
              placeholder="whsec_..."
            />
            {settings.stripe_webhook_secret_masked && !settings.stripe_webhook_secret && (
              <p className="text-zinc-500 text-xs">Current: {settings.stripe_webhook_secret_masked}</p>
            )}
          </div>
        </div>
      </div>

      {/* paywb (S2S Card) Configuration */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-emerald-400" />
            <h3 className="text-lg font-semibold text-white">paywb (Kredi/Banka Kartı)</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-zinc-500 text-xs">{settings.paywb_payment_enabled ? 'Enabled' : 'Disabled'}</span>
            <Switch
              checked={!!settings.paywb_payment_enabled}
              onCheckedChange={(val) => setSettings({ ...settings, paywb_payment_enabled: val })}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Kur Kaynağı</Label>
            <select
              value={settings.rate_source || 'tcmb'}
              onChange={(e) => setSettings({ ...settings, rate_source: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md h-10 px-3"
            >
              <option value="tcmb">TCMB (Merkez Bankası) — otomatik günlük kur</option>
              <option value="manual">Manuel — elle girilen kur</option>
            </select>
            {settings.rate_source === 'tcmb' && settings.tcmb_rate_cache?.rate && (
              <p className="text-emerald-400/80 text-xs">
                Son TCMB kuru: 1 USD = {settings.tcmb_rate_cache.rate} TL
                {settings.tcmb_rate_cache.fetched_at && ` (${new Date(settings.tcmb_rate_cache.fetched_at).toLocaleString('tr-TR')})`}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">
                {settings.rate_source === 'tcmb' ? 'Yedek Kur (TCMB erişilemezse)' : 'USD Kuru (1 USD = ? TL)'}
              </Label>
              <Input
                type="number"
                step="0.01"
                value={settings.usd_try_rate ?? ''}
                onChange={(e) => setSettings({ ...settings, usd_try_rate: parseFloat(e.target.value) || 0 })}
                className="bg-zinc-800 border-zinc-700 text-white text-sm"
                placeholder="47.00"
              />
              <p className="text-zinc-500 text-xs">
                {settings.rate_source === 'tcmb'
                  ? 'TCMB çökerse ve önbellek yoksa bu kullanılır.'
                  : 'Kullanıcı TL görür; kart bu kurla USD olarak çekilir.'}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">FX Güvenlik Payı (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.paywb_fx_margin_pct ?? ''}
                onChange={(e) => setSettings({ ...settings, paywb_fx_margin_pct: parseFloat(e.target.value) || 0 })}
                className="bg-zinc-800 border-zinc-700 text-white text-sm"
                placeholder="0"
              />
              <p className="text-zinc-500 text-xs">Kart FX farkına karşı opsiyonel tampon. 0 = kapalı.</p>
            </div>
          </div>
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-amber-300/80 text-xs">
            Kimlik bilgileri (public_key / terNO) sunucu env'inde tutulur. Min. işlem $10. paywb TRY desteklemez → USD ile çekilir.
          </div>
        </div>
      </div>

      {/* USDT Configuration */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Wallet className="w-5 h-5 text-emerald-400" />
            <h3 className="text-lg font-semibold text-white">USDT Crypto</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-zinc-500 text-xs">{settings.usdt_payment_enabled ? 'Enabled' : 'Disabled'}</span>
            <Switch
              checked={settings.usdt_payment_enabled}
              onCheckedChange={(val) => setSettings({ ...settings, usdt_payment_enabled: val })}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Wallet Address</Label>
              <Input
                value={settings.usdt_wallet_address || ''}
                onChange={(e) => setSettings({ ...settings, usdt_wallet_address: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
                placeholder="T... or 0x..."
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Network</Label>
              <select
                value={settings.usdt_network || 'TRC20'}
                onChange={(e) => setSettings({ ...settings, usdt_network: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md h-10 px-3"
              >
                <option value="TRC20">TRC20 (Tron)</option>
                <option value="ERC20">ERC20 (Ethereum)</option>
                <option value="BEP20">BEP20 (BSC)</option>
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Payment Instructions</Label>
            <Textarea
              value={settings.payment_instructions || ''}
              onChange={(e) => setSettings({ ...settings, payment_instructions: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white min-h-20"
              placeholder="Instructions shown to the user before sending crypto payment..."
            />
          </div>
        </div>
      </div>

      {/* Pending USDT Payments */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Clock className="w-5 h-5 text-amber-400" />
          <h3 className="text-lg font-semibold text-white">Pending Payments</h3>
          {pendingPayments.length > 0 && (
            <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/20">{pendingPayments.length}</Badge>
          )}
        </div>

        {pendingPayments.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="w-10 h-10 text-emerald-500/30 mx-auto mb-2" />
            <p className="text-zinc-500 text-sm">No pending payments</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingPayments.map((payment) => (
              <div key={payment.id} className="bg-zinc-800/50 rounded-lg p-4 flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-white font-medium">{payment.user?.name || 'Unknown'}</p>
                    <Badge className="bg-zinc-700 text-zinc-300 text-[10px]">{payment.payment_method}</Badge>
                  </div>
                  <p className="text-zinc-400 text-sm">{payment.user?.email}</p>
                  <p className="text-emerald-400 font-medium">{payment.amount} {payment.currency}</p>
                  {payment.usdt_tx_hash && (
                    <p className="text-zinc-500 text-xs font-mono mt-1 truncate">TX: {payment.usdt_tx_hash}</p>
                  )}
                  <p className="text-zinc-600 text-xs mt-1">{new Date(payment.created_at).toLocaleString('en-US')}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => verifyPayment(payment.id)}
                    className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
                  >
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Verify
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => rejectPayment(payment.id)}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 text-xs"
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSettingsTab;
